import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, Inbox, CheckCircle, XCircle, Users, RefreshCw, Send, Eye, Filter, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppInbox = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, subscribed: 0, unsubscribed: 0 });
  const [filter, setFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);

  useEffect(() => {
    fetchMessages();
    fetchStats();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchMessages();
      fetchStats();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [filter]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whatsapp/inbox?filter_type=${filter}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whatsapp/inbox/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/whatsapp/inbox/mark-read`, { mark_all: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All messages marked as read');
      fetchMessages();
      fetchStats();
    } catch (error) {
      toast.error('Failed to mark messages as read');
    }
  };

  const handleReply = async () => {
    if (!selectedMessage || !replyText.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    setSendingReply(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/whatsapp/inbox/reply`, {
        phone: selectedMessage.from_number,
        message: replyText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Reply sent successfully!');
      setReplyText('');
      setShowReplyModal(false);
      setSelectedMessage(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply. User may be outside 24-hour window.');
    } finally {
      setSendingReply(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Button variant="ghost" onClick={() => navigate('/')} className="mr-4">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center">
                <Inbox className="mr-3 text-green-600" size={28} />
                WhatsApp Inbox
                {stats.unread > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {stats.unread} new
                  </span>
                )}
              </h1>
              <p className="text-gray-600 mt-1">View incoming messages and opt-in responses</p>
            </div>
          </div>
          
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => { fetchMessages(); fetchStats(); }}>
              <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {stats.unread > 0 && (
              <Button variant="outline" onClick={markAllRead}>
                <CheckCircle size={18} className="mr-2" />
                Mark All Read
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Messages</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Inbox className="text-gray-400" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Unread</p>
                <p className="text-2xl font-bold text-blue-600">{stats.unread}</p>
              </div>
              <Bell className="text-blue-400" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Subscribed</p>
                <p className="text-2xl font-bold text-green-600">{stats.subscribed}</p>
              </div>
              <CheckCircle className="text-green-400" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Unsubscribed</p>
                <p className="text-2xl font-bold text-red-600">{stats.unsubscribed}</p>
              </div>
              <XCircle className="text-red-400" size={32} />
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow-sm border p-2 mb-6">
          <div className="flex space-x-2">
            {[
              { key: 'all', label: 'All Messages' },
              { key: 'unread', label: 'Unread' },
              { key: 'optin', label: 'Opt-in Responses' },
              { key: 'subscribed', label: 'Subscribed' },
              { key: 'unsubscribed', label: 'Unsubscribed' }
            ].map((tab) => (
              <Button
                key={tab.key}
                variant={filter === tab.key ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilter(tab.key)}
                className={filter === tab.key ? 'bg-green-600' : ''}
              >
                {tab.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Messages List */}
        <div className="bg-white rounded-lg shadow-sm border">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="animate-spin mx-auto mb-2" size={32} />
              Loading messages...
            </div>
          ) : messages.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Inbox className="mx-auto mb-2 opacity-50" size={48} />
              <p>No messages found</p>
              <p className="text-sm mt-1">Messages will appear here when users reply to your WhatsApp broadcasts</p>
              
              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200 text-left max-w-md mx-auto">
                <p className="font-medium text-amber-800">Setup Required:</p>
                <p className="text-sm text-amber-700 mt-1">
                  To receive messages, configure the webhook URL in Twilio:
                </p>
                <code className="block mt-2 p-2 bg-white rounded text-xs break-all">
                  {BACKEND_URL}/api/whatsapp/webhook
                </code>
              </div>
            </div>
          ) : (
            <div className="divide-y">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer ${!msg.is_read ? 'bg-blue-50' : ''}`}
                  onClick={() => {
                    setSelectedMessage(msg);
                    setShowReplyModal(true);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        msg.optin_status === 'subscribed' ? 'bg-green-100' :
                        msg.optin_status === 'unsubscribed' ? 'bg-red-100' : 'bg-gray-100'
                      }`}>
                        {msg.optin_status === 'subscribed' ? (
                          <CheckCircle className="text-green-600" size={20} />
                        ) : msg.optin_status === 'unsubscribed' ? (
                          <XCircle className="text-red-600" size={20} />
                        ) : (
                          <MessageCircle className="text-gray-600" size={20} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="font-medium">{msg.profile_name || msg.from_number}</p>
                          {!msg.is_read && (
                            <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">New</span>
                          )}
                          {msg.is_optin_response && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              msg.optin_status === 'subscribed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {msg.optin_status === 'subscribed' ? 'Subscribed' : 'Unsubscribed'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <span>{msg.from_number}</span>
                          {msg.entity_id && (
                            <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-medium text-xs">
                              {msg.entity_id}
                            </span>
                          )}
                          {msg.entity_type && (
                            <span className="text-xs text-gray-400">({msg.entity_type})</span>
                          )}
                        </div>
                        <p className="text-sm mt-1">
                          {msg.button_text || msg.body || '(No text)'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{formatDate(msg.received_at)}</p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMessage(msg);
                          setShowReplyModal(true);
                        }}
                      >
                        <Send size={14} className="mr-1" />
                        Reply
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reply Modal */}
        {showReplyModal && selectedMessage && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <MessageCircle className="mr-2 text-green-600" size={24} />
                Reply to {selectedMessage.profile_name || selectedMessage.from_number}
              </h3>
              
              {/* Original Message */}
              <div className="bg-gray-100 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-500 mb-1">Original message:</p>
                <p className="text-sm">{selectedMessage.button_text || selectedMessage.body || '(No text)'}</p>
                <p className="text-xs text-gray-400 mt-1">{formatDate(selectedMessage.received_at)}</p>
              </div>
              
              {/* Warning about 24-hour window */}
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Replies only work within 24 hours of the user's last message. After that, you must use an approved template.
                </p>
              </div>
              
              {/* Reply Input */}
              <div className="space-y-2 mb-4">
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your reply..."
                  rows={4}
                />
              </div>
              
              <div className="flex space-x-3">
                <Button variant="outline" className="flex-1" onClick={() => {
                  setShowReplyModal(false);
                  setSelectedMessage(null);
                  setReplyText('');
                }}>
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  style={{ backgroundColor: '#25D366' }}
                  onClick={handleReply}
                  disabled={!replyText.trim() || sendingReply}
                >
                  {sendingReply ? 'Sending...' : (
                    <>
                      <Send className="mr-2" size={18} />
                      Send Reply
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppInbox;
